import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Subtask {
  id: string;
  parent_id: string | null;
  title: string;
  description?: string;
  status: string;
  priority?: string;
  difficulty?: string;
  assigned_to?: string;
  due_date?: string;
  brand_id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
}

export const useSubtasks = (parentTaskId: string | undefined) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: subtasks = [], isLoading } = useQuery({
    queryKey: ['subtasks', parentTaskId],
    queryFn: async () => {
      if (!parentTaskId) return [];

      const { data, error } = await supabase
        .from('foundational_tasks')
        .select('*, growth_squad:assigned_to(id, name, alias)')
        .eq('parent_id', parentTaskId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Subtask[];
    },
    enabled: !!parentTaskId,
  });

  const createSubtask = useMutation({
    mutationFn: async (newSubtask: {
      parent_id: string;
      title: string;
      description?: string;
      status?: string;
      priority?: string;
      assigned_to?: string;
      due_date?: string;
      brand_id: string;
      user_id: string;
      source_type?: string;
      sprint_id?: string;
      tag_ids?: string[];
    }) => {
      const { data, error } = await supabase
        .from('foundational_tasks')
        .insert({
          ...newSubtask,
          status: newSubtask.status || 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', parentTaskId] });
      queryClient.invalidateQueries({ queryKey: ['foundational-tasks-all-brands'] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks-all-brands'] });
      queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
      toast({
        title: "Success",
        description: "Subtask created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create subtask",
        variant: "destructive",
      });
      console.error('Error creating subtask:', error);
    },
  });

  const updateSubtask = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Subtask> }) => {
      const { data, error } = await supabase
        .from('foundational_tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', parentTaskId] });
      queryClient.invalidateQueries({ queryKey: ['foundational-tasks-all-brands'] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks-all-brands'] });
      queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update subtask",
        variant: "destructive",
      });
      console.error('Error updating subtask:', error);
    },
  });

  const deleteSubtask = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('foundational_tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onMutate: async (deletedId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['subtasks', parentTaskId] });

      // Snapshot the previous value
      const previousSubtasks = queryClient.getQueryData<Subtask[]>(['subtasks', parentTaskId]);

      // Optimistically update to remove the subtask
      queryClient.setQueryData<Subtask[]>(['subtasks', parentTaskId], (old) =>
        old ? old.filter((subtask) => subtask.id !== deletedId) : []
      );

      // Return context with the snapshotted value
      return { previousSubtasks };
    },
    onError: (error, deletedId, context) => {
      // Rollback to the previous state on error
      if (context?.previousSubtasks) {
        queryClient.setQueryData(['subtasks', parentTaskId], context.previousSubtasks);
      }
      toast({
        title: "Error",
        description: "Failed to delete subtask",
        variant: "destructive",
      });
      console.error('Error deleting subtask:', error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', parentTaskId] });
      queryClient.invalidateQueries({ queryKey: ['foundational-tasks-all-brands'] });
      queryClient.invalidateQueries({ queryKey: ['my-tasks-all-brands'] });
      queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
      toast({
        title: "Success",
        description: "Subtask deleted successfully",
      });
    },
  });

  const completedCount = subtasks.filter(s => s.status === 'done').length;
  const totalCount = subtasks.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return {
    subtasks,
    isLoading,
    createSubtask,
    updateSubtask,
    deleteSubtask,
    completedCount,
    totalCount,
    progressPercentage,
  };
};
